import type { ReactNode } from "react";
import styles from "./FormShell.module.css";

interface FormShellProps {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}

/** Title + subtitle stack, with form contents underneath. */
export function FormShell({ title, subtitle, children }: FormShellProps) {
  return (
    <div className={styles.shell}>
      <div className={styles.head}>
        <h2 className={styles.title}>{title}</h2>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  /** Slot rendered on the right of the label row (e.g. "Forgot?" link). */
  right?: ReactNode;
  children: ReactNode;
}

export function Field({ label, hint, error, htmlFor, right, children }: FieldProps) {
  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        {htmlFor ? (
          <label className={styles.label} htmlFor={htmlFor}>
            {label}
          </label>
        ) : (
          <span className={styles.label}>{label}</span>
        )}
        <div className={styles.grow} />
        {right}
      </div>
      {children}
      {!error && hint && <span className={styles.hint}>{hint}</span>}
      {error && (
        <span className={styles.error} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

interface BannerProps {
  variant: "info" | "danger" | "success" | "waitlist";
  icon?: ReactNode;
  children: ReactNode;
}

/** Small contextual banner used for form-wide errors and the waitlist callout. */
export function Banner({ variant, icon, children }: BannerProps) {
  return (
    <div className={`${styles.banner} ${styles[`banner_${variant}`]}`} role="status">
      {icon && <span className={styles.bannerIcon}>{icon}</span>}
      <span>{children}</span>
    </div>
  );
}
